import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import productService from '../../../services/productService';
import subCategoryService from '../../../services/subCategoryService';
import categoryService from '../../../services/categoryService';
import '../../../pages/Pages.css';
import { API_BASE_URL } from '../../../config/constants';
import './Products.css';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Filter states
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubCategory, setFilterSubCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Add Product Form Data
  const [addFormData, setAddFormData] = useState({
    sub_category_id: '',
    name: '',
    description: '',
    short_description: '',
    brand: '',
    sku: '',
    barcode_type: 'EAN-13',
    price: '',
    cost_price: '',
    sale_price: '',
    sale_start_date: '',
    sale_end_date: '',
    stock_quantity: '',
    low_stock_threshold: '',
    weight: '',
    dimensions: { length: '', width: '', height: '' },
    unit: 'piece',
    items_per_unit: 1,
    is_active: true,
    is_featured: false,
    track_quantity: true,
    allow_backorders: false,
    min_order_quantity: 1,
    max_order_quantity: '',
    tags: [],
    notes: '',
    variants: [],
    images: []
  });

  // Edit Product Form Data
  const [editFormData, setEditFormData] = useState({
    sub_category_id: '',
    name: '',
    description: '',
    short_description: '',
    brand: '',
    sku: '',
    barcode_type: 'EAN-13',
    price: '',
    cost_price: '',
    sale_price: '',
    sale_start_date: '',
    sale_end_date: '',
    stock_quantity: '',
    low_stock_threshold: '',
    weight: '',
    dimensions: { length: '', width: '', height: '' },
    unit: 'piece',
    items_per_unit: 1,
    is_active: true,
    is_featured: false,
    track_quantity: true,
    allow_backorders: false,
    min_order_quantity: 1,
    max_order_quantity: '',
    tags: [],
    notes: '',
    variants: [],
    images: []
  });

  const [newTag, setNewTag] = useState('');
  const [newVariant, setNewVariant] = useState({
    variant_name: '',
    attributes: { storage: '', color: '' },
    price: '',
    cost_price: '',
    stock_quantity: ''
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [autoGenerateSKU, setAutoGenerateSKU] = useState(true);

  // Helper function to get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('data:')) return imagePath;
    
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    if (cleanPath.startsWith('storage/') || cleanPath.startsWith('public/')) {
      return `${API_BASE_URL}${cleanPath}`;
    }
    return `${API_BASE_URL}storage/${cleanPath}`;
  };

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  useEffect(() => {
    if (filterCategory) {
      loadSubCategories(filterCategory);
    } else {
      setSubCategories([]);
      setFilterSubCategory('');
    }
  }, [filterCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, filterSubCategory, filterStatus, searchTerm]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getAllProducts();
      setProducts(response.data);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoryService.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadSubCategories = async (categoryId) => {
    try {
      const response = await subCategoryService.getAllSubCategories();
      const filteredSubCategories = response.data.filter(
        sub => sub.category_id.toString() === categoryId
      );
      setSubCategories(filteredSubCategories);
    } catch (error) {
      console.error('Failed to load sub-categories:', error);
    }
  };

  const generateAutoSKU = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `SKU-${timestamp}-${random}`;
  };

  const handleProductNameChange = (name, formType = 'add') => {
    const setFormData = formType === 'add' ? setAddFormData : setEditFormData;
    const formData = formType === 'add' ? addFormData : editFormData;

    setFormData({
      ...formData,
      name: name
    });

    if (autoGenerateSKU && formType === 'add') {
      const baseSKU = name
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .substring(0, 8);
      const randomSuffix = Math.random().toString(36).substring(2, 6).toUpperCase();
      const generatedSKU = baseSKU ? `${baseSKU}-${randomSuffix}` : generateAutoSKU();

      setAddFormData(prev => ({
        ...prev,
        name: name,
        sku: generatedSKU
      }));
    }
  };

  const handleAutoSKUToggle = (enabled) => {
    setAutoGenerateSKU(enabled);
    if (enabled && addFormData.name) {
      handleProductNameChange(addFormData.name, 'add');
    } else if (!enabled) {
      setAddFormData(prev => ({
        ...prev,
        sku: ''
      }));
    }
  };

  const parseJsonField = (field) => {
    if (!field) return null;
    try {
      let parsed = typeof field === 'string' ? JSON.parse(field) : field;
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
      return parsed;
    } catch (error) {
      console.error('Error parsing JSON field:', error);
      return null;
    }
  };

  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      const primaryImage = product.images.find(img => img.is_primary) || product.images[0];
      return getImageUrl(primaryImage.image_path);
    }
    return null;
  };

  const getTotalStock = (product) => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.reduce((sum, variant) => sum + (variant.stock_quantity || 0), 0);
    }
    return product.stock_quantity || 0;
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = !filterCategory ||
      product.sub_category?.category_id?.toString() === filterCategory;
    const matchesSubCategory = !filterSubCategory ||
      product.sub_category_id.toString() === filterSubCategory;
    const matchesStatus = filterStatus === '' ||
      (filterStatus === 'active' && product.is_active) ||
      (filterStatus === 'inactive' && !product.is_active);
    const matchesSearch = !searchTerm ||
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.brand?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesSubCategory && matchesStatus && matchesSearch;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField.includes('.')) {
      const fields = sortField.split('.');
      aValue = a[fields[0]]?.[fields[1]];
      bValue = b[fields[0]]?.[fields[1]];
    }

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const handleViewProduct = async (productId) => {
    try {
      const response = await productService.getProductById(productId);
      setSelectedProduct(response.data);
      setShowViewModal(true);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load product details', 'error');
    }
  };

  const handleEditProduct = async (product) => {
    setSelectedProduct(product);

    const dimensions = parseJsonField(product.dimensions) || { length: '', width: '', height: '' };
    const tags = parseJsonField(product.tags) || [];

    const processedImages = product.images ? product.images.map(img => ({
      ...img,
      image_data: getImageUrl(img.image_path),
      image_path: img.image_path
    })) : [];

    setEditFormData({
      sub_category_id: product.sub_category_id.toString(),
      name: product.name,
      description: product.description || '',
      short_description: product.short_description || '',
      brand: product.brand || '',
      sku: product.sku || '',
      barcode_type: product.barcode_type || 'EAN-13',
      price: product.price || '',
      cost_price: product.cost_price || '',
      sale_price: product.sale_price || '',
      sale_start_date: product.sale_start_date ? product.sale_start_date.split('T')[0] : '',
      sale_end_date: product.sale_end_date ? product.sale_end_date.split('T')[0] : '',
      stock_quantity: product.stock_quantity || '',
      low_stock_threshold: product.low_stock_threshold || '',
      weight: product.weight || '',
      dimensions: dimensions,
      unit: product.unit || 'piece',
      items_per_unit: product.items_per_unit || 1,
      is_active: product.is_active,
      is_featured: product.is_featured || false,
      track_quantity: product.track_quantity !== undefined ? product.track_quantity : true,
      allow_backorders: product.allow_backorders || false,
      min_order_quantity: product.min_order_quantity || 1,
      max_order_quantity: product.max_order_quantity || '',
      tags: tags,
      notes: product.notes || '',
      variants: product.variants || [],
      images: processedImages
    });

    setShowEditModal(true);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const formData = {
        ...editFormData,
        sub_category_id: parseInt(editFormData.sub_category_id),
        price: parseFloat(editFormData.price) || 0,
        cost_price: parseFloat(editFormData.cost_price) || 0,
        sale_price: parseFloat(editFormData.sale_price) || null,
        stock_quantity: parseInt(editFormData.stock_quantity) || 0,
        low_stock_threshold: parseInt(editFormData.low_stock_threshold) || 0,
        weight: parseFloat(editFormData.weight) || 0,
        items_per_unit: parseInt(editFormData.items_per_unit) || 1,
        min_order_quantity: parseInt(editFormData.min_order_quantity) || 1,
        max_order_quantity: parseInt(editFormData.max_order_quantity) || null,
        dimensions: JSON.stringify(editFormData.dimensions),
        tags: JSON.stringify(editFormData.tags),
        variants: editFormData.variants.map(variant => ({
          ...variant,
          price: parseFloat(variant.price) || 0,
          cost_price: parseFloat(variant.cost_price) || 0,
          stock_quantity: parseInt(variant.stock_quantity) || 0
        }))
      };

      await productService.updateProduct(selectedProduct.id, formData);
      Swal.fire('Success!', 'Product updated successfully', 'success');
      setShowEditModal(false);
      loadProducts();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to update product', 'error');
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();

    if (!addFormData.name.trim()) {
      Swal.fire('Error!', 'Product name is required', 'error');
      return;
    }

    if (!addFormData.sub_category_id) {
      Swal.fire('Error!', 'Please select a sub-category', 'error');
      return;
    }

    if (!addFormData.sku.trim()) {
      Swal.fire('Error!', 'SKU is required', 'error');
      return;
    }

    try {
      const formData = {
        ...addFormData,
        sub_category_id: parseInt(addFormData.sub_category_id),
        price: parseFloat(addFormData.price) || 0,
        cost_price: parseFloat(addFormData.cost_price) || 0,
        sale_price: parseFloat(addFormData.sale_price) || null,
        stock_quantity: parseInt(addFormData.stock_quantity) || 0,
        low_stock_threshold: parseInt(addFormData.low_stock_threshold) || 0,
        weight: parseFloat(addFormData.weight) || 0,
        items_per_unit: parseInt(addFormData.items_per_unit) || 1,
        min_order_quantity: parseInt(addFormData.min_order_quantity) || 1,
        max_order_quantity: parseInt(addFormData.max_order_quantity) || null,
        dimensions: JSON.stringify(addFormData.dimensions),
        tags: JSON.stringify(addFormData.tags),
        variants: addFormData.variants.map(variant => ({
          variant_name: variant.variant_name,
          attributes: variant.attributes,
          price: parseFloat(variant.price) || 0,
          cost_price: parseFloat(variant.cost_price) || 0,
          stock_quantity: parseInt(variant.stock_quantity) || 0
        }))
      };

      await productService.createProduct(formData);
      Swal.fire('Success!', 'Product created successfully', 'success');
      setShowAddModal(false);
      resetAddForm();
      loadProducts();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to create product', 'error');
    }
  };

  const resetAddForm = () => {
    setAddFormData({
      sub_category_id: '',
      name: '',
      description: '',
      short_description: '',
      brand: '',
      sku: '',
      barcode_type: 'EAN-13',
      price: '',
      cost_price: '',
      sale_price: '',
      sale_start_date: '',
      sale_end_date: '',
      stock_quantity: '',
      low_stock_threshold: '',
      weight: '',
      dimensions: { length: '', width: '', height: '' },
      unit: 'piece',
      items_per_unit: 1,
      is_active: true,
      is_featured: false,
      track_quantity: true,
      allow_backorders: false,
      min_order_quantity: 1,
      max_order_quantity: '',
      tags: [],
      notes: '',
      variants: [],
      images: []
    });
    setNewTag('');
    setNewVariant({
      variant_name: '',
      attributes: { storage: '', color: '' },
      price: '',
      cost_price: '',
      stock_quantity: ''
    });
    setImagePreviews([]);
    setAutoGenerateSKU(true);
  };

  const handleDeleteProduct = (product) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete "${product.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await productService.deleteProduct(product.id);
          Swal.fire('Deleted!', 'Product has been deleted.', 'success');
          loadProducts();
        } catch (error) {
          Swal.fire('Error!', error.message || 'Failed to delete product', 'error');
        }
      }
    });
  };

  const handleStatusUpdate = (product) => {
    const newStatus = !product.is_active;
    const action = newStatus ? 'activate' : 'deactivate';

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to ${action} "${product.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus ? '#3085d6' : '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Yes, ${action} it!`
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await productService.updateProductStatus(product.id, newStatus);
          Swal.fire('Updated!', `Product has been ${action}d.`, 'success');
          loadProducts();
        } catch (error) {
          Swal.fire('Error!', error.message || `Failed to ${action} product`, 'error');
        }
      }
    });
  };

  const addTag = (formType) => {
    if (!newTag.trim()) return;

    const setFormData = formType === 'add' ? setAddFormData : setEditFormData;
    const formData = formType === 'add' ? addFormData : editFormData;

    if (!formData.tags.includes(newTag.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      });
    }
    setNewTag('');
  };

  const removeTag = (tagToRemove, formType) => {
    const setFormData = formType === 'add' ? setAddFormData : setEditFormData;
    const formData = formType === 'add' ? addFormData : editFormData;

    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const addVariant = (formType) => {
    if (!newVariant.variant_name.trim()) {
      Swal.fire('Error!', 'Variant name is required', 'error');
      return;
    }

    const setFormData = formType === 'add' ? setAddFormData : setEditFormData;
    const formData = formType === 'add' ? addFormData : editFormData;

    setFormData({
      ...formData,
      variants: [...formData.variants, { ...newVariant }]
    });

    setNewVariant({
      variant_name: '',
      attributes: { storage: '', color: '' },
      price: '',
      cost_price: '',
      stock_quantity: ''
    });
  };

  const removeVariant = (index, formType) => {
    const setFormData = formType === 'add' ? setAddFormData : setEditFormData;
    const formData = formType === 'add' ? addFormData : editFormData;

    setFormData({
      ...formData,
      variants: formData.variants.filter((_, i) => i !== index)
    });
  };

  const handleImageUpload = (e, formType) => {
    const files = Array.from(e.target.files);

    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        Swal.fire('Error!', 'Please select image files only', 'error');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        Swal.fire('Error!', 'Image size should be less than 2MB', 'error');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;

        const setFormData = formType === 'add' ? setAddFormData : setEditFormData;
        const formData = formType === 'add' ? addFormData : editFormData;

        const newImage = {
          image_data: base64String,
          is_primary: formData.images.length === 0
        };

        setFormData({
          ...formData,
          images: [...formData.images, newImage]
        });

        if (formType === 'add') {
          setImagePreviews(prev => [...prev, base64String]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index, formType) => {
    const setFormData = formType === 'add' ? setAddFormData : setEditFormData;
    const formData = formType === 'add' ? addFormData : editFormData;

    const newImages = formData.images.filter((_, i) => i !== index);
    if (formData.images[index].is_primary && newImages.length > 0) {
      newImages[0].is_primary = true;
    }

    setFormData({
      ...formData,
      images: newImages
    });

    if (formType === 'add') {
      setImagePreviews(prev => prev.filter((_, i) => i !== index));
    }
  };

  const setPrimaryImage = (index, formType) => {
    const setFormData = formType === 'add' ? setAddFormData : setEditFormData;
    const formData = formType === 'add' ? addFormData : editFormData;

    const newImages = formData.images.map((img, i) => ({
      ...img,
      is_primary: i === index
    }));

    setFormData({
      ...formData,
      images: newImages
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR'
    }).format(amount || 0);
  };

  const getStockStatus = (quantity, threshold) => {
    if (quantity === 0) return { status: 'out-of-stock', text: 'Out of Stock' };
    if (quantity <= threshold) return { status: 'low-stock', text: 'Low Stock' };
    return { status: 'in-stock', text: 'In Stock' };
  };

  if (loading) {
    return (
      <div className="prod-page-wrapper">
        <div className="prod-page-header">
          <h1 className="prod-page-title">Products</h1>
          <p className="prod-page-subtitle">Manage your product catalog</p>
        </div>
        <div className="prod-loading-state">
          <div className="prod-loading-spinner"></div>
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="prod-page-wrapper">
      <div className="prod-page-header">
        <div className="prod-header-content">
          <div className="prod-header-text">
            <h1 className="prod-page-title">Products</h1>
            <p className="prod-page-subtitle">
              Manage your product catalog ({filteredProducts.length} of {products.length} products)
            </p>
          </div>
          <div className="prod-header-actions">
            <div className="prod-search-box">
              <input
                type="text"
                placeholder="Search products by name, SKU, or brand..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="prod-search-input"
              />
              <span className="prod-search-icon">🔍</span>
            </div>
            <button className="prod-btn prod-btn-secondary" onClick={loadProducts}>
              <span className="prod-btn-icon">🔄</span>
              Refresh
            </button>
            <button
              className="prod-btn prod-btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              <span className="prod-btn-icon">➕</span>
              Add Product
            </button>
          </div>
        </div>
      </div>

      <div className="prod-page-content">
        {/* Filters Section */}
        <div className="prod-filters-section">
          <div className="prod-filter-controls">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="prod-filter-select"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            <select
              value={filterSubCategory}
              onChange={(e) => setFilterSubCategory(e.target.value)}
              className="prod-filter-select"
              disabled={!filterCategory}
            >
              <option value="">All Sub-Categories</option>
              {subCategories.map(subCategory => (
                <option key={subCategory.id} value={subCategory.id}>
                  {subCategory.name}
                </option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="prod-filter-select"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(e.target.value)}
              className="prod-filter-select"
            >
              <option value="5">5 per page</option>
              <option value="10">10 per page</option>
              <option value="20">20 per page</option>
              <option value="50">50 per page</option>
            </select>
          </div>
        </div>

        {/* Products Table */}
        <div className="prod-table-container">
          <table className="prod-table">
            <thead className="prod-table-header">
              <tr>
                <th 
                  className="prod-table-th prod-table-product sortable"
                  onClick={() => handleSort('name')}
                >
                  <div className="prod-table-sort-header">
                    Product
                    <span className="prod-sort-icon">
                      {sortField === 'name' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕️'}
                    </span>
                  </div>
                </th>
                <th className="prod-table-th prod-table-sku">SKU</th>
                <th className="prod-table-th prod-table-category">Category</th>
                <th 
                  className="prod-table-th prod-table-price sortable"
                  onClick={() => handleSort('price')}
                >
                  <div className="prod-table-sort-header">
                    Price
                    <span className="prod-sort-icon">
                      {sortField === 'price' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕️'}
                    </span>
                  </div>
                </th>
                <th 
                  className="prod-table-th prod-table-stock sortable"
                  onClick={() => handleSort('stock_quantity')}
                >
                  <div className="prod-table-sort-header">
                    Stock
                    <span className="prod-sort-icon">
                      {sortField === 'stock_quantity' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕️'}
                    </span>
                  </div>
                </th>
                <th 
                  className="prod-table-th prod-table-status sortable"
                  onClick={() => handleSort('is_active')}
                >
                  <div className="prod-table-sort-header">
                    Status
                    <span className="prod-sort-icon">
                      {sortField === 'is_active' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕️'}
                    </span>
                  </div>
                </th>
                <th className="prod-table-th prod-table-actions">Actions</th>
              </tr>
            </thead>
            <tbody className="prod-table-body">
              {paginatedProducts.map(product => {
                const totalStock = getTotalStock(product);
                const stockStatus = getStockStatus(totalStock, product.low_stock_threshold);
                const productImage = getProductImage(product);

                return (
                  <tr key={product.id} className={`prod-table-row ${!product.is_active ? 'prod-row-inactive' : ''}`}>
                    <td className="prod-table-td prod-table-product-cell">
                      <div className="prod-table-product-info">
                        <div className="prod-table-image-wrapper">
                          {productImage ? (
                            <img
                              src={productImage}
                              alt={product.name}
                              className="prod-table-img"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className="prod-image-placeholder" style={{ display: productImage ? 'none' : 'flex' }}>
                            {product.name.charAt(0).toUpperCase()}
                          </div>
                        </div>
                        <div className="prod-table-product-details">
                          <div className="prod-table-name">{product.name}</div>
                          <div className="prod-table-brand">{product.brand || 'No brand'}</div>
                          {product.is_featured && (
                            <span className="prod-featured-badge">Featured</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="prod-table-td prod-table-sku-cell">
                      <code className="prod-sku-code">{product.sku}</code>
                    </td>
                    <td className="prod-table-td prod-table-category-cell">
                      <div className="prod-category-info">
                        <div className="prod-category-name">
                          {product.sub_category?.category?.name || 'N/A'}
                        </div>
                        <div className="prod-subcategory-name">
                          {product.sub_category?.name || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="prod-table-td prod-table-price-cell">
                      <div className="prod-price-info">
                        <div className="prod-current-price">
                          {formatCurrency(parseFloat(product.sale_price || product.price))}
                        </div>
                        {product.sale_price && product.sale_price !== product.price && (
                          <div className="prod-original-price">
                            {formatCurrency(parseFloat(product.price))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="prod-table-td prod-table-stock-cell">
                      <div className="prod-stock-info">
                        <div className={`prod-stock-status ${stockStatus.status}`}>
                          {stockStatus.text}
                        </div>
                        <div className="prod-stock-quantity">
                          {totalStock} units
                        </div>
                        {product.variants && product.variants.length > 0 && (
                          <div className="prod-variants-count">
                            {product.variants.length} variants
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="prod-table-td prod-table-status-cell">
                      <div className="prod-status-container">
                        <span className={`prod-status-badge ${product.is_active ? 'prod-status-active' : 'prod-status-inactive'}`}>
                          {product.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="prod-table-td prod-table-actions-cell">
                      <div className="prod-table-actions">
                        <button
                          className="prod-action-btn prod-action-view"
                          onClick={() => handleViewProduct(product.id)}
                          title="View Product"
                        >
                          👁️
                        </button>
                        <button
                          className="prod-action-btn prod-action-edit"
                          onClick={() => handleEditProduct(product)}
                          title="Edit Product"
                        >
                          ✏️
                        </button>
                        <button
                          className={`prod-action-btn ${product.is_active ? 'prod-action-deactivate' : 'prod-action-activate'}`}
                          onClick={() => handleStatusUpdate(product)}
                          title={product.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {product.is_active ? '⏸️' : '▶️'}
                        </button>
                        <button
                          className="prod-action-btn prod-action-delete"
                          onClick={() => handleDeleteProduct(product)}
                          title="Delete Product"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredProducts.length === 0 && (
            <div className="prod-empty-state">
              <div className="prod-empty-icon">📦</div>
              <h3 className="prod-empty-title">No Products Found</h3>
              <p className="prod-empty-description">
                {searchTerm || filterCategory || filterSubCategory || filterStatus
                  ? 'No products match your search criteria.'
                  : 'Get started by creating your first product.'
                }
              </p>
              <button
                className="prod-btn prod-btn-primary prod-empty-action"
                onClick={() => setShowAddModal(true)}
              >
                <span className="prod-btn-icon">➕</span>
                Add New Product
              </button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="prod-pagination">
            <div className="prod-pagination-info">
              Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
            </div>

            <div className="prod-pagination-controls">
              <button
                className="prod-pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`prod-pagination-btn ${currentPage === pageNum ? 'prod-pagination-active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                className="prod-pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      {showAddModal && (
        <ProdAddEditModal
          mode="add"
          formData={addFormData}
          setFormData={setAddFormData}
          categories={categories}
          subCategories={subCategories}
          setFilterCategory={setFilterCategory}
          newTag={newTag}
          setNewTag={setNewTag}
          newVariant={newVariant}
          setNewVariant={setNewVariant}
          imagePreviews={imagePreviews}
          onClose={() => { setShowAddModal(false); resetAddForm(); }}
          onSubmit={handleAddProduct}
          onAddTag={() => addTag('add')}
          onRemoveTag={(tag) => removeTag(tag, 'add')}
          onAddVariant={() => addVariant('add')}
          onRemoveVariant={(index) => removeVariant(index, 'add')}
          onImageUpload={(e) => handleImageUpload(e, 'add')}
          onRemoveImage={(index) => removeImage(index, 'add')}
          onSetPrimaryImage={(index) => setPrimaryImage(index, 'add')}
          autoGenerateSKU={autoGenerateSKU}
          onAutoSKUToggle={handleAutoSKUToggle}
          onProductNameChange={(name) => handleProductNameChange(name, 'add')}
          getImageUrl={getImageUrl}
        />
      )}

      {/* Edit Product Modal */}
      {showEditModal && (
        <ProdAddEditModal
          mode="edit"
          formData={editFormData}
          setFormData={setEditFormData}
          categories={categories}
          subCategories={subCategories}
          setFilterCategory={setFilterCategory}
          newTag={newTag}
          setNewTag={setNewTag}
          newVariant={newVariant}
          setNewVariant={setNewVariant}
          imagePreviews={imagePreviews}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateProduct}
          onAddTag={() => addTag('edit')}
          onRemoveTag={(tag) => removeTag(tag, 'edit')}
          onAddVariant={() => addVariant('edit')}
          onRemoveVariant={(index) => removeVariant(index, 'edit')}
          onImageUpload={(e) => handleImageUpload(e, 'edit')}
          onRemoveImage={(index) => removeImage(index, 'edit')}
          onSetPrimaryImage={(index) => setPrimaryImage(index, 'edit')}
          product={selectedProduct}
          getImageUrl={getImageUrl}
        />
      )}

      {/* View Product Modal */}
      {showViewModal && selectedProduct && (
        <ProdViewModal
          product={selectedProduct}
          onClose={() => setShowViewModal(false)}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getImageUrl={getImageUrl}
          parseJsonField={parseJsonField}
        />
      )}
    </div>
  );
};

// View Product Modal Component
const ProdViewModal = ({ product, onClose, formatCurrency, formatDate, getImageUrl, parseJsonField }) => {
  const dimensions = parseJsonField(product.dimensions) || {};
  const tags = parseJsonField(product.tags) || [];

  return (
    <div className="prod-modal-overlay">
      <div className="prod-modal prod-modal-large">
        <div className="prod-modal-header">
          <h3 className="prod-modal-title">Product Details</h3>
          <button className="prod-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="prod-modal-body">
          <div className="prod-detail-view">
            {/* Product Images */}
            {product.images && product.images.length > 0 && (
              <div className="prod-detail-section">
                <h4 className="prod-detail-section-title">Product Images</h4>
                <div className="prod-images-grid">
                  {product.images.map((image, index) => (
                    <div key={image.id} className="prod-image-item">
                      <img
                        src={getImageUrl(image.image_path)}
                        alt={`${product.name} ${index + 1}`}
                        className="prod-detail-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="prod-image-placeholder" style={{ display: 'none' }}>
                        {product.name.charAt(0)}
                      </div>
                      {image.is_primary && <div className="prod-primary-badge">Primary</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="prod-detail-section">
              <h4 className="prod-detail-section-title">Basic Information</h4>
              <div className="prod-detail-grid">
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Name:</label>
                  <span className="prod-detail-value">{product.name}</span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">SKU:</label>
                  <span className="prod-detail-value">{product.sku}</span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Brand:</label>
                  <span className="prod-detail-value">{product.brand || 'N/A'}</span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Category:</label>
                  <span className="prod-detail-value">{product.sub_category?.category?.name || 'N/A'} → {product.sub_category?.name || 'N/A'}</span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Status:</label>
                  <span className={`prod-status-badge ${product.is_active ? 'prod-status-active' : 'prod-status-inactive'}`}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Featured:</label>
                  <span className="prod-detail-value">{product.is_featured ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>

            <div className="prod-detail-section">
              <h4 className="prod-detail-section-title">Pricing & Inventory</h4>
              <div className="prod-detail-grid">
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Price:</label>
                  <span className="prod-detail-value">{formatCurrency(parseFloat(product.price))}</span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Cost Price:</label>
                  <span className="prod-detail-value">{formatCurrency(parseFloat(product.cost_price))}</span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Sale Price:</label>
                  <span className="prod-detail-value">{product.sale_price ? formatCurrency(parseFloat(product.sale_price)) : 'N/A'}</span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Sale Period:</label>
                  <span className="prod-detail-value">
                    {product.sale_start_date ? formatDate(product.sale_start_date) : 'N/A'}
                    {' to '}
                    {product.sale_end_date ? formatDate(product.sale_end_date) : 'N/A'}
                  </span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Stock Quantity:</label>
                  <span className="prod-detail-value">{product.stock_quantity}</span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Low Stock Threshold:</label>
                  <span className="prod-detail-value">{product.low_stock_threshold}</span>
                </div>
              </div>
            </div>

            {product.variants && product.variants.length > 0 && (
              <div className="prod-detail-section">
                <h4 className="prod-detail-section-title">Variants ({product.variants.length})</h4>
                <div className="prod-variants-table">
                  <table className="prod-variants-inner-table">
                    <thead>
                      <tr>
                        <th>Variant Name</th>
                        <th>SKU</th>
                        <th>Attributes</th>
                        <th>Price</th>
                        <th>Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.variants.map((variant, index) => {
                        const attributes = parseJsonField(variant.attributes) || {};
                        return (
                          <tr key={variant.id}>
                            <td>{variant.variant_name}</td>
                            <td><code>{variant.variant_sku}</code></td>
                            <td>
                              {Object.entries(attributes).map(([key, value]) => (
                                <div key={key}><strong>{key}:</strong> {value}</div>
                              ))}
                            </td>
                            <td>{formatCurrency(parseFloat(variant.price))}</td>
                            <td>{variant.stock_quantity}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="prod-detail-section">
              <h4 className="prod-detail-section-title">Additional Information</h4>
              <div className="prod-detail-grid">
                <div className="prod-detail-item prod-detail-full">
                  <label className="prod-detail-label">Short Description:</label>
                  <span className="prod-detail-value">{product.short_description || 'N/A'}</span>
                </div>
                <div className="prod-detail-item prod-detail-full">
                  <label className="prod-detail-label">Full Description:</label>
                  <span className="prod-detail-value">{product.description || 'N/A'}</span>
                </div>
                <div className="prod-detail-item prod-detail-full">
                  <label className="prod-detail-label">Notes:</label>
                  <span className="prod-detail-value">{product.notes || 'N/A'}</span>
                </div>
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Tags:</label>
                  <span className="prod-detail-value">{tags.length > 0 ? tags.join(', ') : 'N/A'}</span>
                </div>
                {dimensions && (dimensions.length || dimensions.width || dimensions.height) && (
                  <div className="prod-detail-item">
                    <label className="prod-detail-label">Dimensions:</label>
                    <span className="prod-detail-value">
                      {dimensions.length ? `L: ${dimensions.length}cm` : ''}
                      {dimensions.width ? ` W: ${dimensions.width}cm` : ''}
                      {dimensions.height ? ` H: ${dimensions.height}cm` : ''}
                    </span>
                  </div>
                )}
                <div className="prod-detail-item">
                  <label className="prod-detail-label">Weight:</label>
                  <span className="prod-detail-value">{product.weight ? `${product.weight} kg` : 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="prod-modal-footer">
          <button className="prod-btn prod-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Add/Edit Product Modal Component
const ProdAddEditModal = ({
  mode, formData, setFormData, categories, subCategories, setFilterCategory,
  newTag, setNewTag, newVariant, setNewVariant, imagePreviews,
  onClose, onSubmit, onAddTag, onRemoveTag, onAddVariant, onRemoveVariant,
  onImageUpload, onRemoveImage, onSetPrimaryImage, product,
  autoGenerateSKU, onAutoSKUToggle, onProductNameChange, getImageUrl
}) => {
  return (
    <div className="prod-modal-overlay prod-modal-fullscreen">
      <div className="prod-modal prod-modal-extra-large">
        <div className="prod-modal-header">
          <h3 className="prod-modal-title">{mode === 'add' ? 'Add New Product' : `Edit Product - ${product?.name}`}</h3>
          <button className="prod-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="prod-modal-body">
            <div className="prod-form-scrollable">
              <div className="prod-form">
                {/* Basic Information Section */}
                <div className="prod-form-section">
                  <h4 className="prod-form-section-title">Basic Information</h4>
                  <div className="prod-form-grid-3">
                    <div className="prod-form-group">
                      <label className="prod-form-label">Category *</label>
                      <select
                        value={formData.sub_category_id ? categories.find(c =>
                          subCategories.some(s => s.id === parseInt(formData.sub_category_id) && s.category_id === c.id)
                        )?.id || '' : ''}
                        onChange={(e) => {
                          setFilterCategory(e.target.value);
                          setFormData({ ...formData, sub_category_id: '' });
                        }}
                        className="prod-form-select"
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Sub-Category *</label>
                      <select
                        value={formData.sub_category_id}
                        onChange={(e) => setFormData({ ...formData, sub_category_id: e.target.value })}
                        className="prod-form-select"
                        required
                        disabled={!categories.find(c =>
                          subCategories.some(s => s.category_id === c.id)
                        )}
                      >
                        <option value="">Select Sub-Category</option>
                        {subCategories.map(subCategory => (
                          <option key={subCategory.id} value={subCategory.id}>
                            {subCategory.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Product Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => {
                          if (mode === 'add' && onProductNameChange) {
                            onProductNameChange(e.target.value);
                          } else {
                            setFormData({ ...formData, name: e.target.value });
                          }
                        }}
                        className="prod-form-input"
                        required
                        placeholder="Enter product name"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Brand</label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                        className="prod-form-input"
                        placeholder="Enter brand name"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">SKU *</label>
                      <div className="prod-sku-input-group">
                        {mode === 'add' && (
                          <div className="prod-sku-toggle">
                            <label className="prod-checkbox-label">
                              <input
                                type="checkbox"
                                checked={autoGenerateSKU}
                                onChange={(e) => onAutoSKUToggle(e.target.checked)}
                                className="prod-checkbox-input"
                              />
                              <span className="prod-checkbox-custom"></span>
                              Auto-generate SKU
                            </label>
                          </div>
                        )}
                        <input
                          type="text"
                          value={formData.sku}
                          onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                          className={`prod-form-input ${mode === 'add' && autoGenerateSKU ? 'prod-input-disabled' : ''}`}
                          required
                          placeholder="Product SKU"
                          disabled={mode === 'add' && autoGenerateSKU}
                        />
                        {mode === 'add' && autoGenerateSKU && (
                          <button
                            type="button"
                            className="prod-btn prod-btn-secondary prod-btn-small"
                            onClick={() => {
                              const newSKU = `SKU-${Date.now().toString().slice(-6)}-${Math.random().toString(36).substring(2, 5).toUpperCase()}`;
                              setFormData({ ...formData, sku: newSKU });
                            }}
                            title="Generate new SKU"
                          >
                            🔄
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Barcode Type</label>
                      <select
                        value={formData.barcode_type}
                        onChange={(e) => setFormData({ ...formData, barcode_type: e.target.value })}
                        className="prod-form-select"
                      >
                        <option value="EAN-13">EAN-13</option>
                        <option value="UPC-A">UPC-A</option>
                        <option value="CODE-128">CODE-128</option>
                        <option value="QR-CODE">QR Code</option>
                      </select>
                    </div>
                  </div>

                  <div className="prod-form-group prod-form-full">
                    <label className="prod-form-label">Short Description</label>
                    <textarea
                      value={formData.short_description}
                      onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                      className="prod-form-textarea"
                      rows="2"
                      placeholder="Brief product description..."
                    />
                  </div>

                  <div className="prod-form-group prod-form-full">
                    <label className="prod-form-label">Full Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="prod-form-textarea"
                      rows="4"
                      placeholder="Detailed product description..."
                    />
                  </div>
                </div>

                {/* Images Section */}
                <div className="prod-form-section">
                  <h4 className="prod-form-section-title">Product Images</h4>
                  <div className="prod-image-upload-section">
                    <div className="prod-image-upload-header">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={onImageUpload}
                        className="prod-image-upload-input"
                        id="prod-image-upload"
                      />
                      <label htmlFor="prod-image-upload" className="prod-btn prod-btn-secondary">
                        📁 Upload Images
                      </label>
                      <span className="prod-image-upload-hint">Max 10 images, 2MB each</span>
                    </div>

                    <div className="prod-image-previews-grid">
                      {formData.images.map((image, index) => (
                        <div key={index} className="prod-image-preview-item">
                          <img
                            src={mode === 'edit' && image.image_path ? getImageUrl(image.image_path) : image.image_data}
                            alt={`Preview ${index + 1}`}
                            className="prod-image-preview-img"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const placeholder = e.target.nextSibling;
                              if (placeholder) {
                                placeholder.style.display = 'flex';
                              }
                            }}
                          />
                          <div className="prod-image-placeholder" style={{ display: 'none' }}>
                            {formData.name ? formData.name.charAt(0).toUpperCase() : 'P'}
                          </div>
                          <div className="prod-image-actions">
                            <button
                              type="button"
                              className={`prod-btn prod-btn-small ${image.is_primary ? 'prod-btn-primary' : 'prod-btn-secondary'}`}
                              onClick={() => onSetPrimaryImage(index)}
                            >
                              {image.is_primary ? '⭐ Primary' : 'Set Primary'}
                            </button>
                            <button
                              type="button"
                              className="prod-btn prod-btn-delete prod-btn-small"
                              onClick={() => onRemoveImage(index)}
                            >
                              🗑️
                            </button>
                          </div>
                          {image.is_primary && <div className="prod-primary-indicator">Primary</div>}
                        </div>
                      ))}
                      {formData.images.length === 0 && (
                        <div className="prod-no-images-placeholder">
                          <div className="prod-no-images-icon">📷</div>
                          <p>No images uploaded yet</p>
                          <small>Upload product images to display here</small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pricing Section */}
                <div className="prod-form-section">
                  <h4 className="prod-form-section-title">Pricing & Inventory</h4>
                  <div className="prod-form-grid-4">
                    <div className="prod-form-group">
                      <label className="prod-form-label">Price (Rs) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="prod-form-input"
                        required
                        min="0"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Cost Price (Rs)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.cost_price}
                        onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
                        className="prod-form-input"
                        min="0"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Sale Price (Rs)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.sale_price}
                        onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                        className="prod-form-input"
                        min="0"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Sale Start Date</label>
                      <input
                        type="date"
                        value={formData.sale_start_date}
                        onChange={(e) => setFormData({ ...formData, sale_start_date: e.target.value })}
                        className="prod-form-input"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Sale End Date</label>
                      <input
                        type="date"
                        value={formData.sale_end_date}
                        onChange={(e) => setFormData({ ...formData, sale_end_date: e.target.value })}
                        className="prod-form-input"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Stock Quantity *</label>
                      <input
                        type="number"
                        value={formData.stock_quantity}
                        onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                        className="prod-form-input"
                        required
                        min="0"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Low Stock Threshold</label>
                      <input
                        type="number"
                        value={formData.low_stock_threshold}
                        onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                        className="prod-form-input"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping & Measurements Section */}
                <div className="prod-form-section">
                  <h4 className="prod-form-section-title">Shipping & Measurements</h4>
                  <div className="prod-form-grid-4">
                    <div className="prod-form-group">
                      <label className="prod-form-label">Weight (kg)</label>
                      <input
                        type="number"
                        step="0.001"
                        value={formData.weight}
                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                        className="prod-form-input"
                        min="0"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Dimensions - Length (cm)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.dimensions.length}
                        onChange={(e) => setFormData({
                          ...formData,
                          dimensions: { ...formData.dimensions, length: e.target.value }
                        })}
                        className="prod-form-input"
                        min="0"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Dimensions - Width (cm)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.dimensions.width}
                        onChange={(e) => setFormData({
                          ...formData,
                          dimensions: { ...formData.dimensions, width: e.target.value }
                        })}
                        className="prod-form-input"
                        min="0"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Dimensions - Height (cm)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.dimensions.height}
                        onChange={(e) => setFormData({
                          ...formData,
                          dimensions: { ...formData.dimensions, height: e.target.value }
                        })}
                        className="prod-form-input"
                        min="0"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Unit</label>
                      <select
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        className="prod-form-select"
                      >
                        <option value="piece">Piece</option>
                        <option value="kg">Kilogram</option>
                        <option value="g">Gram</option>
                        <option value="l">Liter</option>
                        <option value="ml">Milliliter</option>
                        <option value="pack">Pack</option>
                      </select>
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Items per Unit</label>
                      <input
                        type="number"
                        value={formData.items_per_unit}
                        onChange={(e) => setFormData({ ...formData, items_per_unit: e.target.value })}
                        className="prod-form-input"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                {/* Variants Section */}
                <div className="prod-form-section">
                  <h4 className="prod-form-section-title">Product Variants</h4>
                  <div className="prod-variant-form">
                    <div className="prod-form-grid-3">
                      <div className="prod-form-group">
                        <label className="prod-form-label">Variant Name</label>
                        <input
                          type="text"
                          value={newVariant.variant_name}
                          onChange={(e) => setNewVariant({ ...newVariant, variant_name: e.target.value })}
                          className="prod-form-input"
                          placeholder="e.g., 128GB - Blue"
                        />
                      </div>
                      <div className="prod-form-group">
                        <label className="prod-form-label">Storage</label>
                        <input
                          type="text"
                          value={newVariant.attributes.storage}
                          onChange={(e) => setNewVariant({
                            ...newVariant,
                            attributes: { ...newVariant.attributes, storage: e.target.value }
                          })}
                          className="prod-form-input"
                          placeholder="e.g., 128GB"
                        />
                      </div>
                      <div className="prod-form-group">
                        <label className="prod-form-label">Color</label>
                        <input
                          type="text"
                          value={newVariant.attributes.color}
                          onChange={(e) => setNewVariant({
                            ...newVariant,
                            attributes: { ...newVariant.attributes, color: e.target.value }
                          })}
                          className="prod-form-input"
                          placeholder="e.g., Blue"
                        />
                      </div>
                      <div className="prod-form-group">
                        <label className="prod-form-label">Price (Rs)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newVariant.price}
                          onChange={(e) => setNewVariant({ ...newVariant, price: e.target.value })}
                          className="prod-form-input"
                          min="0"
                        />
                      </div>
                      <div className="prod-form-group">
                        <label className="prod-form-label">Cost Price (Rs)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newVariant.cost_price}
                          onChange={(e) => setNewVariant({ ...newVariant, cost_price: e.target.value })}
                          className="prod-form-input"
                          min="0"
                        />
                      </div>
                      <div className="prod-form-group">
                        <label className="prod-form-label">Stock Quantity</label>
                        <input
                          type="number"
                          value={newVariant.stock_quantity}
                          onChange={(e) => setNewVariant({ ...newVariant, stock_quantity: e.target.value })}
                          className="prod-form-input"
                          min="0"
                        />
                      </div>
                    </div>
                    <button type="button" className="prod-btn prod-btn-secondary" onClick={onAddVariant}>
                      ➕ Add Variant
                    </button>
                  </div>

                  {formData.variants.length > 0 && (
                    <div className="prod-variants-list">
                      <h5 className="prod-variants-title">Added Variants ({formData.variants.length})</h5>
                      {formData.variants.map((variant, index) => (
                        <div key={index} className="prod-variant-item">
                          <div className="prod-variant-item-header">
                            <strong>{variant.variant_name}</strong>
                            <button
                              type="button"
                              className="prod-btn prod-btn-delete prod-btn-small"
                              onClick={() => onRemoveVariant(index)}
                            >
                              🗑️
                            </button>
                          </div>
                          <div className="prod-variant-item-details">
                            <span>Price: Rs{parseFloat(variant.price).toFixed(2)}</span>
                            <span>Stock: {variant.stock_quantity}</span>
                            <span>Storage: {variant.attributes.storage || 'N/A'}</span>
                            <span>Color: {variant.attributes.color || 'N/A'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Settings Section */}
                <div className="prod-form-section">
                  <h4 className="prod-form-section-title">Settings</h4>
                  <div className="prod-form-grid-4">
                    <div className="prod-form-group">
                      <label className="prod-form-label">Minimum Order Quantity</label>
                      <input
                        type="number"
                        value={formData.min_order_quantity}
                        onChange={(e) => setFormData({ ...formData, min_order_quantity: e.target.value })}
                        className="prod-form-input"
                        min="1"
                      />
                    </div>

                    <div className="prod-form-group">
                      <label className="prod-form-label">Maximum Order Quantity</label>
                      <input
                        type="number"
                        value={formData.max_order_quantity}
                        onChange={(e) => setFormData({ ...formData, max_order_quantity: e.target.value })}
                        className="prod-form-input"
                        min="1"
                      />
                    </div>

                    <div className="prod-form-group prod-form-full">
                      <div className="prod-checkbox-group">
                        <label className="prod-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="prod-checkbox-input"
                          />
                          <span className="prod-checkbox-custom"></span>
                          Active Product
                        </label>

                        <label className="prod-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.is_featured}
                            onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                            className="prod-checkbox-input"
                          />
                          <span className="prod-checkbox-custom"></span>
                          Featured Product
                        </label>

                        <label className="prod-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.track_quantity}
                            onChange={(e) => setFormData({ ...formData, track_quantity: e.target.checked })}
                            className="prod-checkbox-input"
                          />
                          <span className="prod-checkbox-custom"></span>
                          Track Quantity
                        </label>

                        <label className="prod-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.allow_backorders}
                            onChange={(e) => setFormData({ ...formData, allow_backorders: e.target.checked })}
                            className="prod-checkbox-input"
                          />
                          <span className="prod-checkbox-custom"></span>
                          Allow Backorders
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tags Section */}
                <div className="prod-form-section">
                  <h4 className="prod-form-section-title">Tags</h4>
                  <div className="prod-tags-section">
                    <div className="prod-tag-input">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddTag())}
                        className="prod-form-input"
                        placeholder="Enter tag and press Enter"
                      />
                      <button type="button" className="prod-btn prod-btn-secondary" onClick={onAddTag}>
                        Add Tag
                      </button>
                    </div>

                    <div className="prod-tags-list">
                      {formData.tags.map((tag, index) => (
                        <span key={index} className="prod-tag">
                          {tag}
                          <button type="button" onClick={() => onRemoveTag(tag)}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="prod-form-section">
                  <h4 className="prod-form-section-title">Additional Notes</h4>
                  <div className="prod-form-group prod-form-full">
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="prod-form-textarea"
                      rows="3"
                      placeholder="Any additional notes or instructions..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="prod-modal-footer">
            <button type="button" className="prod-btn prod-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="prod-btn prod-btn-primary">
              {mode === 'add' ? 'Create Product' : 'Update Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Products;